// @ts-nocheck

import classNames from 'classnames';
import PropTypes from 'prop-types';
import * as queryString from 'query-string';
import * as R from 'ramda';
import * as React from 'react';
import BranchLoader from '../../../common/components/core/BranchLoader';
import Button from '../../../common/components/core/Button';
import Link from '../../../common/components/core/Link';
import { LEGAL_PRIVACY_POLICY_LINK } from '../../../common/support/gdprConstants';
import LoggedoutActions from '../../../main/actions/LoggedoutActions';
import { ImageService } from '../../../main/services/ImageService/ImageService';
import LoggedoutStore from '../../../main/stores/LoggedoutStore';
import { getCurrentTimeInUTC } from '../../../main/support/dateUtils';
import { TermsAndConditions } from '../../components/TermsAndConditions/TermsAndConditions';
import AuthCheckbox from '../AuthCheckbox';
import PasswordInput from '../PasswordInput';
import ScreenLinkExpired from '../ScreenLinkExpired';
import { inputTooLong, passwordsMatch } from '../ScreenSignupValidation';
import { emotionStyles } from './ScreenSetPassword.styles';

const country = window.App.context.country || {};
const BUTTON_TEXT = 'Save Password';
const validationParams = [
  {
    title: 'Lowercase Letter',
    type: 'lowercase',
    regex: /[a-z]/,
  },
  {
    title: 'Capital Letter',
    type: 'capital',
    regex: /[A-Z]/,
  },
  {
    title: 'Number',
    type: 'number',
    regex: /[0-9]/,
  },
  {
    title: 'Symbol',
    type: 'symbol',
    regex: /[|\\/~^:,;?!&%$@*+#]/,
  },
  {
    title: 'Min of 8 characters',
    type: 'min',
    regex: /^.{8,}$/,
  },
];
type Props = {
  params: {
    reset_key?: string;
    accept_key?: string;
  };
  location: {
    search: string;
  };
  match: any;
};
type State = {
  passwordKeyState: string | null | undefined;
  password: string | null | undefined;
  password2: string | null | undefined;
  errors: {
    password: string | null | undefined;
    password2: string | null | undefined;
  };
  marketingOptOut: boolean;
  resetKey: string | null | undefined;
  resetType: string | null | undefined;
  lowercase: boolean | null | undefined;
  capital: boolean | null | undefined;
  number: boolean | null | undefined;
  symbol: boolean | null | undefined;
  min: boolean | null | undefined;
  passwordChecked: boolean | null | undefined;
  password2Checked: boolean | null | undefined;
  passwordMatch: boolean | null | undefined;
  termsAndConditionsIsChecked: boolean;
  showTermsError: boolean;
  errorFootnotes: {
    password: string | null | undefined;
    password2: string | null | undefined;
  };
};

export default class ScreenSetPassword extends React.Component<Props, State> {
  static contextTypes = {
    router: PropTypes.object,
  };

  constructor(props: Props) {
    super(props);
    const resetKey = this.props.match.params.accept_key || this.props.match.params.reset_key;
    const resetType = this.props.match.params.accept_key ? 'agency-accept' : 'login-reset';
    this.state = {
      resetKey,
      resetType,
      password: null,
      password2: null,
      passwordKeyState: LoggedoutStore.isPasswordKeyValid(resetKey, resetType),
      errors: LoggedoutStore.getErrors(),
      marketingOptOut: false,
      lowercase: false,
      capital: false,
      number: false,
      symbol: false,
      min: false,
      passwordChecked: false,
      password2Checked: false,
      passwordMatch: false,
      termsAndConditionsIsChecked: false,
      showTermsError: false,
      errorFootnotes: {
        password: null,
        password2: null,
        terms: null,
      },
    };
  }

  componentWillMount() {
    LoggedoutStore.addChangeListener(this._onChange);
  }

  componentWillUnmount() {
    LoggedoutStore.removeChangeListener(this._onChange);
    this.refs.form.removeEventListener('invalid', this._preventDefault, true);
  }

  componentDidMount() {
    this.refs.form.addEventListener('invalid', this._preventDefault, true);
  }

  _onChange = () => {
    const passwordKeyState = LoggedoutStore.isPasswordKeyValid(this.state.resetKey, this.state.resetType);
    const errors = LoggedoutStore.getErrors();
    this.setState({
      passwordKeyState,
      errors,
    });
  };

  _preventDefault = (ev: React.SyntheticEvent) => {
    ev.preventDefault();
  };

  _onPasswordChange = (ev: {
    target: {
      value: string;
    };
    value: string | null | undefined;
    error: boolean | null | undefined;
  }) => {
    if (ev.target.value && typeof ev.target.value === 'string') {
      this.setState({
        password: ev.target.value,
      });

      this._validatePasswordParams(ev.target.value);

      LoggedoutActions.clearErrors();
    }
  };

  _onPassword2Change = (ev: {
    target: {
      value: string;
    };
    value: string | null | undefined;
    error: boolean | null | undefined;
  }) => {
    if (ev.target.value && typeof ev.target.value === 'string') {
      this.setState({
        password2: ev.target.value,
      });

      this._passwordsMatch(ev.target.value);

      LoggedoutActions.clearErrors();
    }
  };

  _onMarketingChange = (ev: React.SyntheticEvent) => {
    this.setState({
      marketingOptOut: ev.target.checked,
    });
  };

  _ctaClick = () => {
    const { location } = this.props;

    if (!this.state.termsAndConditionsIsChecked) {
      this.setState({
        ...this.state,
        showTermsError: true,
      });
      return;
    }

    const data = R.pick(['password', 'password2', 'marketingOptOut'], this.state);

    if (!country.hasDataProtectionPolicy) {
      delete data.marketingOptOut;
    }

    LoggedoutActions.setPassword(
      {
        key: this.state.resetKey,
        type: this.state.resetType,
        terms_consent_date: getCurrentTimeInUTC(),
        ...data,
      },
      queryString.parse(location.search).next_path
    );
  };

  _validatePasswordParams = (input: string) => {
    validationParams.map((rule) => {
      const currentRule = rule.type;
      this.setState({
        [currentRule]: !!rule.regex.test(input) && !inputTooLong(input) ? true : false,
        password2Checked: false,
      });
    });

    this._validatePassword(input);
  };

  _renderPasswordSuccessIcon = () => {
    let validCount = 0;
    validationParams.forEach((rule) => {
      const currentRule = rule.type;

      if (!!this.state[currentRule]) {
        validCount += 1;
      }
    });
    return validCount === 5 ? true : false;
  };

  _renderPasswordValidation = (key: string) => {
    const validationLabels = validationParams.map((rule) => {
      const currentRule = rule.type;
      return (
        <li key={rule.type} className="validation-li" data-testid={rule.type}>
          {!!this.state[currentRule] ? (
            <i className="material-icons valid">check</i>
          ) : (
            <i className="material-icons invalid">close</i>
          )}
          <span
            className={`validation-label ${classNames({
              'validation-met': !!this.state[currentRule],
            })}`}
          >
            {rule.title}
          </span>
        </li>
      );
    });

    /**
     * The password input is the only one whose footer prop doesn't run _renderErrors (to determine frontend errors or backend errors),
     * so this logic is repeated here to cover all scenarios for password input footers.
     * Order of footnote rendering:
     * 1. if user clicks on CTA button without focusing on input field and input field is empty, render errors returned from backend (state.errors)
     * 2. if user has typed anything into the input field, render the validationLabels
     * 3. if user's input does not pass front-end validations, render errors (errorFootnotes)
     * 4. else render the default footer
     */
    if (this.state.errors[key]) {
      return this.state.errors[key];
    } else if (this.state.password) {
      return <ul className="validation-rules">{validationLabels}</ul>;
    } else if (this.state.errorFootnotes[key]) {
      return this.state.errorFootnotes[key];
    } else {
      return (
        <div className="default-password-footer">
          Must contain a capital and lowercase letter, a number, a symbol, and min of 8 characters.
        </div>
      );
    }
  };

  // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
  _validatePassword = (input: string) => {
    const currentErrors = { ...this.state.errorFootnotes };
    currentErrors.password = !this._renderPasswordSuccessIcon() ? 'Please specify a password.' : '';
    this.setState({
      errorFootnotes: currentErrors,
      passwordChecked: true,
    });
  };

  // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
  _passwordsMatch = (input: string) => {
    const currentErrors = { ...this.state.errorFootnotes };
    currentErrors.password2 = !passwordsMatch(this.state.password, this.state.password2) ? "Passwords don't match." : '';
    this.setState({
      passwordMatch: !!passwordsMatch(this.state.password, this.state.password2) ? true : false,
      password2Checked: true,
      errorFootnotes: currentErrors,
    });
  };

  _handleToggleTerms = (isChecked: boolean) => {
    this.setState({
      termsAndConditionsIsChecked: isChecked,
      showTermsError: false,
    });
  };

  // this is here to determine which to show in the input footnote; the frontend errors (errorFootnotes) or
  // the errors returned from the backend (this.state.errors) which are generated on CTA button click.
  _renderErrors = (key: string) => {
    return this.state.errorFootnotes[key] ? this.state.errorFootnotes[key] : this.state.errors[key];
  };

  _fieldHasError = (key: string) => {
    return !!this.state.errorFootnotes[key];
  };

  _invalidKeyContent = () => (
    <div>
      <ScreenLinkExpired />
    </div>
  );

  _validKeyContent = () => (
    <div className="login-form titles-above-inputs">
      <PasswordInput
        onChange={this._onPasswordChange}
        value={this.state.password}
        footnote={this._renderPasswordValidation('password')}
        name="password"
        type="password"
        label="New Password"
        testid="new-password"
        inputClassName="set-password-input"
        hasError={this._fieldHasError('password')}
        inputValid={!!this._renderPasswordSuccessIcon()}
        inputChecked={!!this.state.passwordChecked}
        pasteDisabled={true}
        displayShowHide={true}
      />
      <PasswordInput
        onChange={this._onPassword2Change}
        value={this.state.password2}
        name="password2"
        type="password"
        label="Confirm Password"
        testid="confirm-new-password"
        hasError={this._fieldHasError('password2')}
        footnote={this._renderErrors('password2')}
        inputClassName="set-password-input"
        inputValid={!!this.state.passwordMatch}
        inputChecked={!!this.state.password2Checked}
      />

      <TermsAndConditions
        className={emotionStyles.termsAndConditionsContainer}
        termsAccepted={this.state.termsAndConditionsIsChecked}
        onToggleTerms={this._handleToggleTerms}
        showRequiredAsterisk
        showErrorText={this.state.showTermsError}
      />

      <div className="h_ta-c btn-create-password">
        {country.hasDataProtectionPolicy ? (
          <AuthCheckbox
            checked={this.state.marketingOptOut}
            onChange={this._onMarketingChange}
            className="sign-up-checkbox marketing-checkbox h_mt-10"
            labelClassName="marketing-checkbox__label"
            label={
              <span className="h_fw-300">
                By submitting your information, Branch will add you to its email list to keep you posted about Branch
                products and promotions in accordance with our privacy policy.{' '}
                <span className="h_fw-400">If you do not want to be added to that list, please check this box</span>.
              </span>
            }
            id="marketing-communications__invited-user"
          />
        ) : null}
        <Button className="btn-signup create-password" onClick={this._ctaClick} testid="set-new-password">
          {BUTTON_TEXT}
        </Button>
      </div>
    </div>
  );

  _customerSupportText = () => (
    <div className="h_ta-c support-footer">
      Questions? We are here to{' '}
      <Link href="https://support.branch.io" testid="set-password__help-link" className="h_pstart-5">
        help
      </Link>
      .
    </div>
  );

  _marketingDisclaimer = () => (
    <div className="sign-up-privacy-notice h_pt-20 loggedout--footer create-password-footer">
      {`By clicking "${BUTTON_TEXT}," you acknowledge that Branch may send you
        information about our products and promotions. For information
        about how to opt-out of our promotional emails, click `}
      <Link testid="sign-up-privacy-notice" href={LEGAL_PRIVACY_POLICY_LINK}>
        here
      </Link>
      .
    </div>
  );

  render() {
    let body = null;

    switch (this.state.passwordKeyState) {
      case 'PASSWORD_KEY_LOADING':
        body = <BranchLoader size="medium" className="create-password-loader" />;
        break;

      case 'PASSWORD_KEY_INVALID':
        body = (
          <div>
            {this._invalidKeyContent()}
            {this._customerSupportText()}
          </div>
        );
        break;

      case 'PASSWORD_KEY_VALID':
        body = (
          <div>
            {this._validKeyContent()}
            {this._customerSupportText()}
            {!country.hasDataProtectionPolicy ? this._marketingDisclaimer() : null}
          </div>
        );
        break;

      default:
        body = <div className="loggedout--reset-link-error text-center">An unexpected error occurred.</div>;
    }

    return (
      <div className="loggedout--container">
        <img src={ImageService.getImageUrl('/static/images/branch-logos/branch-logo_dark.svg')} />
        <div className="login-container">
          <div className="auth-header create-password">
            <h1 className="create-account h_fw-700">Create New Password</h1>
          </div>
          <form role="form" ref="form" onSubmit={this._preventDefault} className="create-password-form">
            {body}
          </form>
        </div>
      </div>
    );
  }
}
